import React, { useState } from 'react'
import { connect as connectRedux, useSelector } from 'react-redux'
import { Map } from 'immutable'
import { historyRedirect } from '../../services/navigation/index'
import { URL_AI_CHAT } from '../../services/url/constants'
import { selectAuth } from '../../services/auth/selectors'
import Button, { TYPES, SIZES } from '../Button/index'
import ArrowUp from '../Icon/icons/ArrowUp/index'
import VoiceMode from '../Icon/icons/VoiceMode/index'
import { ICON_COLORS } from '../Icon/index'
import { NewPip } from '../OracleCommons'
import { makeIcon } from '../OracleCommons/icons'
import type { IconName } from '../OracleCommons/icons'
import { useDivinationCollectionsByTypeQuery } from '../OraclePage/queries.generated'

export const DISPLAY_NAME = 'PromotionGaiaDivination'

interface PromotionGaiaDivinationProps {
  staticText: Map<string, string>
  history?: Record<string, unknown>
  oracleVoiceAccess?: boolean
}

const DIVINATION_OPTIONS: {
  feature: string
  textKey: string
  fallback: string
  shortTextKey: string
  shortFallback: string
  cardTestId: string
  icon: IconName
}[] = [
  {
    feature: 'tarot', textKey: 'personalizedTarot', fallback: 'Personalized Tarot', shortTextKey: 'shortTarot', shortFallback: 'Tarot', cardTestId: 'card-tarot', icon: 'tarot-cards',
  },
  {
    feature: 'oracleCard', textKey: 'oracleReadings', fallback: 'Oracle Readings', shortTextKey: 'shortOracle', shortFallback: 'Oracle', cardTestId: 'card-oracle', icon: 'oracle-cards',
  },
  {
    feature: 'horoscope', textKey: 'dailyHoroscope', fallback: 'Daily Horoscope', shortTextKey: 'shortHoroscope', shortFallback: 'Horoscope', cardTestId: 'card-horoscope', icon: 'horoscope',
  },
]

const PromotionGaiaDivination = ({
  staticText, history, oracleVoiceAccess,
}: PromotionGaiaDivinationProps) => {
  const [inputValue, setInputValue] = useState('')
  const auth = useSelector(selectAuth)
  const jwt = auth.get('jwt')

  const { data: oracleData } = useDivinationCollectionsByTypeQuery({
    variables: { type: 'oracle_cards' },
    context: { jwt },
  })
  const { data: tarotData } = useDivinationCollectionsByTypeQuery({
    variables: { type: 'tarot' },
    context: { jwt },
  })

  const oracleBackImageUrl = oracleData?.divinationCollectionsByType?.[0]?.backImageUrl
  const tarotBackImageUrl = tarotData?.divinationCollectionsByType?.[0]?.backImageUrl

  const cardImageUrls: Record<string, string | undefined> = {
    tarot: tarotBackImageUrl,
    oracleCard: oracleBackImageUrl,
    horoscope: tarotBackImageUrl, // reuse tarot until horoscope has its own
  }

  return (
    <div className="promotion-gaia-divination">
      <div className="promotion-gaia-divination__content">
        <div className="promotion-gaia-divination__header">
          <NewPip label={staticText.get('newBadge', 'New')} />
          <h2 className="promotion-gaia-divination__title">
            {staticText.get('dailyDivinationTitle', 'Daily Divination')}
          </h2>
        </div>
        <p className="promotion-gaia-divination__description">
          {staticText.get('divinationDescription', 'Follow your curiosity - explore, reflect, and expand with daily readings')}
        </p>
        <div className="promotion-gaia-divination__cards">
          {DIVINATION_OPTIONS.map(({
            feature, textKey, fallback, cardTestId,
          }) => {
            const imageUrl = cardImageUrls[feature]
            return (
              <div key={cardTestId} className="promotion-gaia-divination__card" data-testid={cardTestId}>
                {imageUrl && (
                  <img
                    className="promotion-gaia-divination__card-image"
                    src={imageUrl}
                    alt={staticText.get(textKey, fallback)}
                  />
                )}
              </div>
            )
          })}
        </div>
        <div className="promotion-gaia-divination__buttons">
          {DIVINATION_OPTIONS.map(({
            feature, textKey, fallback, shortTextKey, shortFallback, icon,
          }) => (
            <button
              key={feature}
              className="promotion-gaia-divination__button"
              type="button"
              onClick={() => historyRedirect({ history, url: URL_AI_CHAT, query: { feature } })}
            >
              {makeIcon(icon, '40px')}
              <span className="promotion-gaia-divination__button-text--long">
                {staticText.get(textKey, fallback)}
              </span>
              <span className="promotion-gaia-divination__button-text--short" aria-hidden="true">
                {staticText.get(shortTextKey, shortFallback)}
              </span>
            </button>
          ))}
        </div>
        <div className="promotion-gaia-divination__card-labels">
          {DIVINATION_OPTIONS.map(({ feature, textKey, fallback }, index) => (
            <React.Fragment key={feature}>
              {index > 0 && <span aria-hidden="true">{'\u2022'}</span>}
              <span>{staticText.get(textKey, fallback)}</span>
            </React.Fragment>
          ))}
        </div>
        <div className="promotion-gaia-divination__input-section">
          <p className="promotion-gaia-divination__sage-prompt">
            {staticText.get('sagePrompt', "Discuss anything with Sage, Gaia's AI Guide -")}
          </p>
          <div className="promotion-gaia-divination__input-wrapper">
            <input
              className="promotion-gaia-divination__input"
              placeholder={staticText.get('divinationPlaceholder', 'What is divination?...')}
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && inputValue) {
                  historyRedirect({ history, url: URL_AI_CHAT, query: { prompt: inputValue } })
                }
              }}
            />
            <Button
              type={TYPES.DEFAULT}
              size={SIZES.XSMALL}
              className="promotion-gaia-divination__submit-button"
              submit={false}
              onClick={() => {
                if (inputValue) {
                  historyRedirect({ history, url: URL_AI_CHAT, query: { prompt: inputValue } })
                } else {
                  historyRedirect({
                    history,
                    url: URL_AI_CHAT,
                    query: oracleVoiceAccess ? { voiceMode: true } : {},
                  })
                }
              }}
            >
              {
                !inputValue && oracleVoiceAccess
                  ? <VoiceMode size={22} color={ICON_COLORS.WHITE} />
                  : <ArrowUp size={22} color={ICON_COLORS.WHITE} />
              }
            </Button>
          </div>
        </div>
      </div>
    </div>
  )
}

export { PromotionGaiaDivination }

interface ReduxState {
  staticText: Map<string, unknown>
  oracle: Map<string, unknown>
}

const Connected = connectRedux(
  (state: ReduxState) => {
    const { staticText, oracle } = state
    return {
      staticText: staticText.getIn(['data', 'promotionGaiaOracle', 'data'], Map()),
      oracleVoiceAccess: oracle.getIn(['access', 'aiSpeech'], false),
    }
  },
)(PromotionGaiaDivination)

export default function PromotionGaiaDivinationConnected (props: Record<string, unknown>) {
  return React.createElement(Connected, props)
}
